import { Button } from '@acpaas-ui/react-components';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReset } from '../actions/fetch.actions';
import SelectField from '../components/forms/SelectField';
import TextField from '../components/forms/TextField';
import {
  STAKEHOLDER_DOSSIER_PATHS,
  STAKEHOLDER_ROLES_CASETAFEL_OPTIONS,
  STAKEHOLDER_ROLES_CLARIFICATION_OPTIONS,
  STAKEHOLDER_ROLES_CONTACTMOMENTS_OPTIONS,
  STAKEHOLDER_ROLES_PROCEDURE_OPTIONS
} from '../constants/dossier.constants';
import { MODALS } from '../constants/modals.constants';
import { PERMISSION_TO_TEXT } from '../constants/permissions.constants';
import formValidators from '../form-validators';
import { useForm } from '../hooks/useForm.hook';
import { useModal } from '../hooks/useModal.hook';
import { useResourceInput } from '../hooks/useResourceInput';
import { CASETAFEL_STAKEHOLDERS_RESOURCE } from '../resources/casetafel.stakeholders.resource';
import { CLARIFICATION_STAKEHOLDERS_RESOURCE } from '../resources/clarification.stakeholders.resource';
import { CONTACTMOMENT_STAKEHOLDERS_RESOURCE } from '../resources/contactmoments.stakeholders.resource';
import { PROCEDURE_STAKEHOLDERS_RESOURCE } from '../resources/procedure.stakeholders.resource';
import casetafelsService from '../services/api/casetafels.service';
import clarificationsService from '../services/api/clarifications.service';
import contactMomentsService from '../services/api/contactmoments.service';
import procedureService from '../services/api/procedure.service';
import DeleteWarningModal from './DeleteWarningModal';
import Modal from './Modal';
import ModalTitle from './ModalTitle';

const StakeholderDetailModal = ({
  isOpen,
  dossierId,
  dossierPath,
  itemId,
  data,
  write,
  read,
  onUpdated,
  onClose,
  onDeleted
}) => {
  const modalContentRef = useRef(null);
  const dispatch = useDispatch();
  const { data: stakeholder, isSubmitting, error, reset, input, submitStart, submitOk, submitError } = useForm(data);
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => stakeholder?.[field],
    setValue: (value, field, options = {}) => {
      let updateFn = () => {};
      let stakeholdersResource;
      switch (dossierPath) {
        case STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION:
          updateFn = clarificationsService.updateStakeholder;
          stakeholdersResource = CLARIFICATION_STAKEHOLDERS_RESOURCE;
          break;
        case STAKEHOLDER_DOSSIER_PATHS.CASETABLE:
        case STAKEHOLDER_DOSSIER_PATHS.COMMISSION:
          updateFn = casetafelsService.updateStakeholder;
          stakeholdersResource = CASETAFEL_STAKEHOLDERS_RESOURCE;
          break;
        case STAKEHOLDER_DOSSIER_PATHS.PROCEDURE:
          updateFn = procedureService.updateStakeholder;
          stakeholdersResource = PROCEDURE_STAKEHOLDERS_RESOURCE;
          break;
        case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS:
          updateFn = contactMomentsService.updateStakeholder;
          stakeholdersResource = CONTACTMOMENT_STAKEHOLDERS_RESOURCE;
          break;
        case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD:
          return onUpdated({ ...stakeholder, [field]: value });
        default:
          break;
      }
      submitStart();
      updateFn(
        dossierId,
        itemId,
        stakeholder.id,
        { [field]: value },
        {
          onSuccess: (resp) => {
            submitOk();
            input(value, field);
            let newInvolved = [];
            if (dossierPath === STAKEHOLDER_DOSSIER_PATHS.PROCEDURE) {
              newInvolved = resp?.[dossierPath]?.involved;
            } else {
              // when commission data is saved under caseTable
              newInvolved = resp?.[
                dossierPath === STAKEHOLDER_DOSSIER_PATHS.COMMISSION ? STAKEHOLDER_DOSSIER_PATHS.CASETABLE : dossierPath
              ]?.find((item) => item.id === itemId)?.involved;
            }
            onUpdated(newInvolved, 'involved', itemId);
            dispatch(
              fetchReset({
                key:
                  dossierPath === STAKEHOLDER_DOSSIER_PATHS.PROCEDURE
                    ? stakeholdersResource.key
                    : `${stakeholdersResource.key}_${itemId}`
              })
            );
            options.onSuccess();
          },
          onError: (message) => {
            submitError();
            options.onError(message);
          }
        }
      );
    },
    validators: {
      role: [STAKEHOLDER_DOSSIER_PATHS.PROCEDURE, STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS].includes(dossierPath)
        ? () => ''
        : formValidators.required
    },
    onCancel: reset
  };
  const roleInput = useResourceInput('role', resource);
  const deleteModal = useModal(MODALS.STAKEHOLDER_DELETE_WARNING_MODAL);

  const getRoleOptions = () => {
    switch (dossierPath) {
      case STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION:
        return STAKEHOLDER_ROLES_CLARIFICATION_OPTIONS;
      case STAKEHOLDER_DOSSIER_PATHS.CASETABLE:
      case STAKEHOLDER_DOSSIER_PATHS.COMMISSION:
        return STAKEHOLDER_ROLES_CASETAFEL_OPTIONS;
      case STAKEHOLDER_DOSSIER_PATHS.PROCEDURE:
        return STAKEHOLDER_ROLES_PROCEDURE_OPTIONS;
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS:
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD:
        return STAKEHOLDER_ROLES_CONTACTMOMENTS_OPTIONS;
      default:
        return [];
    }
  };

  const handleDelete = (onError, onSuccess) => {
    let removeFn = () => {};
    let stakeholdersResource;
    switch (dossierPath) {
      case STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION:
        removeFn = clarificationsService.removeStakeholder;
        stakeholdersResource = CLARIFICATION_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.CASETABLE:
      case STAKEHOLDER_DOSSIER_PATHS.COMMISSION:
        removeFn = casetafelsService.removeStakeholder;
        stakeholdersResource = CASETAFEL_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.PROCEDURE:
        removeFn = procedureService.removeStakeholder;
        stakeholdersResource = PROCEDURE_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS:
        removeFn = contactMomentsService.removeStakeholder;
        stakeholdersResource = CONTACTMOMENT_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD:
        return onDeleted(data);
      default:
        break;
    }
    removeFn(dossierId, itemId, stakeholder.id, {
      onError,
      onSuccess: (resp) => {
        let newInvolved = [];
        if (dossierPath === STAKEHOLDER_DOSSIER_PATHS.PROCEDURE) {
          newInvolved = resp?.[dossierPath]?.involved;
        } else {
          // when commission data is saved under caseTable
          newInvolved = resp?.[
            dossierPath === STAKEHOLDER_DOSSIER_PATHS.COMMISSION ? STAKEHOLDER_DOSSIER_PATHS.CASETABLE : dossierPath
          ]?.find((item) => item.id === itemId)?.involved;
        }
        // const newInvolved = resp?.[dossierPath]?.find((item) => item.id === itemId)?.involved;
        onUpdated(newInvolved?.filter((i) => !!i) ?? [], 'involved', itemId);
        dispatch(
          fetchReset({
            key:
              dossierPath === STAKEHOLDER_DOSSIER_PATHS.PROCEDURE
                ? stakeholdersResource.key
                : `${stakeholdersResource.key}_${itemId}`
          })
        );
        onSuccess();
        onClose();
      }
    });
  };

  if (!read) {
    return null;
  }

  return (
    <>
      <Modal
        ref={modalContentRef}
        title={<ModalTitle title={`${data?.firstname ?? ''} ${data?.lastname ?? ''}`.trim()} />}
        isOpen={isOpen}
        disableConfirm
        disableCancel
        onClose={onClose}
        isSubmitting={isSubmitting}
        extraFooterActions={
          write && (
            <Button type="danger" outline onClick={deleteModal.onOpen}>
              Verwijderen
            </Button>
          )
        }
      >
        <div className="row">
          <div className="col-xs-12 col-md-6 u-margin-bottom-md">
            <SelectField
              label={
                dossierPath === STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION
                  ? 'Rol voor vraagverheldering'
                  : dossierPath === STAKEHOLDER_DOSSIER_PATHS.CASETABLE
                  ? 'Rol voor casetafel'
                  : dossierPath === STAKEHOLDER_DOSSIER_PATHS.COMMISSION
                  ? 'Rol voor commissie'
                  : [STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS, STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD].includes(
                      dossierPath
                    )
                  ? 'Rol voor contactmoment'
                  : 'Rol voor opvolging'
              }
              options={getRoleOptions()}
              required={[
                STAKEHOLDER_DOSSIER_PATHS.CASETABLE,
                STAKEHOLDER_DOSSIER_PATHS.COMMISSION,
                STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION
              ].includes(dossierPath)}
              isSubmitting={isSubmitting}
              disableSearch
              parentElement={modalContentRef.current}
              {...roleInput}
              readOnly={!write}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-6 u-margin-bottom">
            <TextField label="Voornaam" readOnly value={stakeholder?.firstname} />
          </div>
          <div className="col-xs-12 col-md-6 u-margin-bottom">
            <TextField label="Familienaam" readOnly value={stakeholder?.lastname} />
          </div>
          <div className="col-xs-12 col-md-6 u-margin-bottom">
            <TextField label="E-mail" readOnly value={stakeholder?.email} />
          </div>
          <div className="col-xs-12 col-md-6 u-margin-bottom">
            <TextField label="Organisatie" readOnly value={stakeholder?.organisationName} />
          </div>
          <div className="col-xs-12 col-md-6 u-margin-bottom">
            <TextField label="Rechten" readOnly value={PERMISSION_TO_TEXT?.[stakeholder?.organisationRole] ?? ''} />
          </div>
        </div>
        {error && <small className="u-block u-margin-top-sm u-text-danger">{error}</small>}
      </Modal>
      <DeleteWarningModal
        {...deleteModal}
        warningText="Ben je zeker dat je deze betrokkene wilt verwijderen?"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default StakeholderDetailModal;
